/**
 * Every glyph the app draws.
 *
 * One file rather than the sibling repo's two, because the seam it splits on
 * does not exist here: there is no set of glyphs that describes a CAR — a car is
 * its artwork and its numbers — so every pictogram in this product operates the
 * app. When a second family arrives it gets its own file; inventing that seam
 * now would be two files where one is honest.
 *
 * Drawn in the shared `Icon` frame: 1.75px strokes, square caps, mitred joins,
 * per §10. Shape names and props are the contract; the path data is not.
 */

import Icon from './Icon';

/**
 * The way back out of a car, and the deck's backward step. A chevron with a
 * mitred point rather than a rounded one — the brand's own corner, at 24px.
 */
export function BackIcon(props) {
    return (
        <Icon {...props}>
            <path d="m14.5 4.5-7.5 7.5 7.5 7.5" />
        </Icon>
    );
}

/**
 * The other way along the deck. `BackIcon` mirrored, and DRAWN rather than
 * transformed: a chevron is four numbers, and a CSS flip is a second way of
 * saying what a `d` attribute already says — one a right-to-left stylesheet
 * would then have to know about.
 */
export function ForwardIcon(props) {
    return (
        <Icon {...props}>
            <path d="m9.5 4.5 7.5 7.5-7.5 7.5" />
        </Icon>
    );
}

/** A note about how something works. Never an error — those are drawn as words. */
export function InfoIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5.5" />
            <path d="M12 7.6v.4" />
        </Icon>
    );
}

/** The way out of a match: a cog, because what it acts on is the match itself. */
export function GearIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="12" r="3.2" />
            <path d="M12 3.2v2.2M12 18.6v2.2M3.2 12h2.2M18.6 12h2.2M5.8 5.8l1.6 1.6M16.6 16.6l1.6 1.6M18.2 5.8l-1.6 1.6M7.4 16.6l-1.6 1.6" />
        </Icon>
    );
}

/** Leaving a sheet. */
export function CloseIcon(props) {
    return (
        <Icon {...props}>
            <path d="m5.5 5.5 13 13M18.5 5.5l-13 13" />
        </Icon>
    );
}

/**
 * The filter affordance in the header.
 *
 * Three tracks with a slider each, at different positions — deliberately not a
 * funnel. What it opens is a set of INDEPENDENT adjustments; a funnel promises
 * one narrowing pipe, which is the wrong mental model for three groups that
 * AND together.
 *
 * The handles are squares rather than circles, which is the §10 divergence from
 * every other sliders glyph on a phone: this set has no round corners in it.
 */
export function SlidersIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.5 7h4.5M13 7h7.5M3.5 17h7M15.5 17h5" />
            <path d="M8.5 4.5h4v5h-4zM11 14.5h4v5h-4z" />
        </Icon>
    );
}

/** The chosen one of a set — an active filter pill. */
export function CheckIcon(props) {
    return (
        <Icon {...props}>
            <path d="m5 12.5 4.5 4.5L19 7" />
        </Icon>
    );
}

/**
 * Sending this car somewhere (FR-09).
 *
 * Three nodes and the two lines between them: the network share glyph, which is
 * the one people already read as "send this on". Not an up-arrow out of a tray —
 * that is iOS's own mark and it means something narrower on every other
 * platform.
 */
export function ShareIcon(props) {
    return (
        <Icon {...props}>
            <path d="M17.5 3.5h3v3h-3zM3.5 9.5h3v3h-3zM17.5 15.5h3v3h-3z" />
            <path d="m17.5 5.5-11 5M6.5 12l11 5" />
        </Icon>
    );
}

/**
 * The picture a card travels as.
 *
 * A framed rectangle with a horizon in it. Deliberately not a camera: nothing
 * here is photographed, and a camera glyph on a share row promises the reader is
 * about to take one.
 */
export function PictureIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.5 5.5h17v13h-17z" />
            <path d="m4 16 5-5 3.5 3.5L16 11l4 4" />
            <path d="M8 9h.01" />
        </Icon>
    );
}

/**
 * A wide frame with two lines of caption under it: the feed post.
 *
 * It has to be told apart from `PictureIcon` at 24px, and the thing that
 * actually differs between the two destinations is the SHAPE — 4:5 for a story
 * or a message bubble, 1.91:1 for a feed — so the frame is drawn wide and short
 * where the other is nearly square. The caption lines are what say "post"
 * rather than "picture": a feed card is a picture somebody types under.
 *
 * Deliberately not Facebook's own mark. A branded glyph in a destination list
 * promises a Facebook integration, and this is a share sheet handing the OS a
 * correctly-shaped file — the reader picks the app themselves.
 */
export function PostIcon(props) {
    return (
        <Icon {...props}>
            <rect x="3.5" y="4" width="17" height="10" rx="1.5" />
            <path d="M4 17.5h12M4 20.5h7" />
        </Icon>
    );
}

/** Two links of a chain: the address, and the only share that unfurls. */
export function LinkIcon(props) {
    return (
        <Icon {...props}>
            <path d="M10.3 13.7a3.6 3.6 0 0 0 5.1 0l2.6-2.6a3.6 3.6 0 0 0-5.1-5.1l-1.3 1.3" />
            <path d="M13.7 10.3a3.6 3.6 0 0 0-5.1 0L6 12.9a3.6 3.6 0 0 0 5.1 5.1l1.3-1.3" />
        </Icon>
    );
}

/**
 * Keeping the app.
 *
 * A phone with an arrow coming down into it — the app moving onto the device,
 * which is what installing a PWA actually is. Deliberately not a disc or a
 * cloud: nothing is downloaded and nothing is saved to a file, and both glyphs
 * promise a file manager.
 */
export function InstallIcon(props) {
    return (
        <Icon {...props}>
            <path d="M6 2.75h12v18.5H6z" />
            <path d="M12 7v6.5m0 0 2.5-2.5M12 13.5 9.5 11" />
            <path d="M9.5 17.75h5" />
        </Icon>
    );
}

/**
 * The garage, in the tab bar.
 *
 * A roller door: a lintel and three slats. Not a car — the tab is a PLACE, and
 * a car glyph beside a screen full of cars says nothing the screen does not.
 */
export function GarageIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3 10 12 4.5 21 10v9.5H3z" />
            <path d="M6.5 19.5v-7h11v7M6.5 15.5h11" />
        </Icon>
    );
}

/**
 * The duel, in the tab bar.
 *
 * A chequered flag: the pole, the cloth, and two filled squares on the diagonal.
 * The one glyph in the set drawn with a FILL as well as a stroke, and it is
 * worth the exception — a chequer outlined in 1.75px strokes at 22px is four
 * empty boxes, which reads as a grid rather than as a flag.
 *
 * Not a controller, a die or a trophy. The tab is a PLACE in a product about
 * 90s racing games, and all three of those glyphs promise a different genre of
 * thing to do when you get there.
 */
export function FlagIcon(props) {
    return (
        <Icon {...props}>
            <path d="M5.5 3v18" />
            <path d="M5.5 4.5h14v10h-14z" />
            <path d="M5.5 4.5h7v5h-7zM12.5 9.5h7v5h-7z" fill="currentColor" stroke="none" />
        </Icon>
    );
}

/**
 * The card game, in the tab bar.
 *
 * Two cards, offset, cut with the same square corners as everything else in the
 * set. Not a hand fanned into an arc: an arc needs curves, and a curve in this
 * icon language reads as a different product's glyph sitting in the bar.
 *
 * Deliberately not a suit, a chip or a deck box. A spade or a club would promise
 * a standard 52-card game, which this is not — the cards are the catalogue.
 */
export function CardsIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4.5 7.5h8v12h-8z" />
            <path d="M11.5 4.5h8v12h-8z" />
        </Icon>
    );
}

/**
 * The empty and the failed state's mark.
 *
 * A wheel: five spokes and a rim, drawn straight-edged. It is the one shape in
 * the product that is a car part without being a car, which is what an empty
 * garage needs — a car glyph on a screen that says "no legends match" would be
 * drawing the thing that is missing.
 */
export function WheelIcon(props) {
    return (
        <Icon {...props}>
            <circle cx="12" cy="12" r="8.5" />
            <circle cx="12" cy="12" r="2.5" />
            <path d="M12 3.5v6M20.5 12h-6M12 20.5v-6M3.5 12h6" />
        </Icon>
    );
}

/**
 * The duel — two cars on a strip.
 *
 * Two lanes with a start line across them — the strip, seen from above, which is
 * the picture the duel actually draws. Not a balance scale and not two arrows: a
 * scale promises a verdict this product does not issue, and opposing arrows are
 * the universal glyph for "swap", which is a control ON that screen rather than
 * the screen itself.
 *
 * It marked the duel's tab until SPEC-008 PR-8.13 took the tab away, and now
 * marks the two links that replaced it — the same glyph for the same
 * destination, which is the whole reason it is a shape rather than a label.
 */
export function CompareIcon(props) {
    return (
        <Icon {...props}>
            <path d="M3.5 4.5h17v15h-17z" />
            <path d="M12 4.5v15" />
            <path d="M3.5 8.5h17" />
        </Icon>
    );
}

/**
 * A disclosure's own arrow.
 *
 * Points DOWN at rest and is rotated by the sheet when its `<details>` opens,
 * rather than being two glyphs — an open and a closed chevron are the same
 * shape and shipping both would be the icon set carrying a state.
 */
export function ChevronIcon(props) {
    return (
        <Icon {...props}>
            <path d="m5.5 9 6.5 6.5L18.5 9" />
        </Icon>
    );
}

/**
 * A car that has not been won yet (SPEC-010).
 *
 * A padlock, and the only glyph in the set that describes a STATE rather than
 * an action. It earns the exception the same way the rarity ribbon earns its
 * colour: a silhouette on a shelf of photographs is ambiguous — a picture that
 * failed to load looks exactly like one that is being withheld — and the lock is
 * what tells the reader which of the two they are looking at before they read a
 * word.
 *
 * A closed shackle, drawn narrower than the body so the shape survives at the
 * 20px the card actually draws it at.
 */
export function LockIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4.5 10.5h15v10h-15z" />
            <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
        </Icon>
    );
}

/**
 * The clock's tick, on and off (SPEC-007 PR-7.6).
 *
 * A speaker cone with two arcs, and its muted twin with a cross where the arcs
 * were. DRAWN as two glyphs rather than one with a slash laid over it: a
 * diagonal rule across a cone is a third path at a third angle, and at 20px on
 * near-black it reads as a smudge rather than as "off". The cross is the same
 * mark `CloseIcon` uses, which is the one shape in this set that already means
 * "not this".
 */
export function SoundOnIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4.5 9.5h3.5l4.5-4v13l-4.5-4H4.5z" />
            <path d="M16 9a4.5 4.5 0 0 1 0 6" />
            <path d="M18.5 6.5a8 8 0 0 1 0 11" />
        </Icon>
    );
}

export function SoundOffIcon(props) {
    return (
        <Icon {...props}>
            <path d="M4.5 9.5h3.5l4.5-4v13l-4.5-4H4.5z" />
            <path d="m16 9.5 5 5" />
            <path d="m21 9.5-5 5" />
        </Icon>
    );
}
